import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { purchaseOrdersAPI, formatApiError } from '../utils/api'

export const usePurchaseOrders = (params = {}) => {
  return useQuery(
    ['purchaseOrders', params],
    () => purchaseOrdersAPI.getAll(params).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: true, // Refetch when window gains focus
      refetchOnMount: true, // Always refetch on mount
    }
  )
}

export const usePurchaseOrder = (id) => {
  return useQuery(
    ['purchaseOrder', id],
    () => purchaseOrdersAPI.getById(id).then(res => res.data),
    {
      enabled: !!id,
      refetchOnWindowFocus: true, // Refetch when window gains focus
      refetchOnMount: true, // Always refetch on mount
    }
  )
}

export const useCreatePurchaseOrder = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (formData) => purchaseOrdersAPI.create(formData),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['purchaseOrders'])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Orden de compra creada exitosamente', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useUpdatePurchaseOrder = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    ({ id, formData }) => purchaseOrdersAPI.update(id, formData),
    {
      onSuccess: (data, variables) => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['purchaseOrders'])
        queryClient.invalidateQueries(['purchaseOrder', variables.id])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Orden de compra actualizada exitosamente', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useUpdatePurchaseOrderStatus = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    ({ id, status }) => purchaseOrdersAPI.updateStatus(id, status),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['purchaseOrders'])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Estado de orden de compra actualizado', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useDeletePurchaseOrder = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (id) => purchaseOrdersAPI.delete(id),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['purchaseOrders'])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        queryClient.invalidateQueries(['reports'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Orden de compra eliminada exitosamente', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const usePurchaseOrderPdf = (id) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    () => purchaseOrdersAPI.getPdfUrl(id),
    {
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}
